import {
  EditorState,
} from 'draft-js';
import {
  DateTimeFormFieldOptions,
  FormFieldArrayOptions,
  GeneralFormFields,
  ImagePickerFieldOption,
  InputFieldOptions,
  LocationFormFieldOptions,
  MapLocation,
  NestedFormFieldOptions,
  RichTextFieldOptions,
  SelectFieldOptions,
  TextAreaFieldOptions,
  ToggleFormFieldOptions,
} from '../types';

export type FieldProps<T, K extends keyof T> = FieldPropsCommon<T, K> & (
  InputFieldValue<T, K>
  | TextAreaFieldValue<T, K>
  | ImagePickerFieldValue<T, K>
  | NestedFormFieldValue<T, K>
  | ArrayFormFieldFieldValue<T, K>
  | SelectFieldValue<T, K>
  | RichTextFieldValue<T, K>
  | DateTimeFieldValue<T, K>
  | ToggleFieldValue<T, K>
  | LocationFieldValue<T, K>
  | ColorFieldValue
);

export type FieldPropsTypes<T, K extends keyof T> = (
  (FieldPropsCommon<T, K> & InputFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & TextAreaFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & ImagePickerFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & NestedFormFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & ArrayFormFieldFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & SelectFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & RichTextFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & DateTimeFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & ToggleFieldValue<T, K>)
  | (FieldPropsCommon<T, K> & LocationFieldValue<T, K>)
);

export interface FieldPropsCommon<T, K extends keyof T> {
  title: string;
  location: string;
  disabled?: boolean;
  level?: number;
}

export interface InputFieldValue<T, K extends keyof T, V = string> {
  type: GeneralFormFields.INPUT_FIELD;
  fieldOptions: InputFieldOptions;
}

export function isInputProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & InputFieldValue<T, K> {
  return props.type === GeneralFormFields.INPUT_FIELD;
}

export interface TextAreaFieldValue<T, K extends keyof T, V = string> {
  type: GeneralFormFields.TEXT_AREA;
  fieldOptions: TextAreaFieldOptions;
}

export function isTextAreaProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & TextAreaFieldValue<T, K> {
  return props.type === GeneralFormFields.TEXT_AREA;
}

export interface ImagePickerFieldValue<T, K extends keyof T, V = FieldFile[]> {
  type: GeneralFormFields.FILE_PICKER;
  fieldOptions: ImagePickerFieldOption;
}

export function isImagePickerProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & ImagePickerFieldValue<T, K> {
  return props.type === GeneralFormFields.FILE_PICKER;
}

export interface NestedFormFieldValue<T, K extends keyof T, V = T[K]> {
  type: GeneralFormFields.NESTED_FORM;
  fieldOptions: NestedFormFieldOptions<T[K]>;
}

export function isNestedFormProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & NestedFormFieldValue<T, K> {
  return props.type === GeneralFormFields.NESTED_FORM;
}

export interface ArrayFormFieldFieldValue<T, K extends keyof T, V = T[K]> {
  type: GeneralFormFields.FIELD_ARRAY;
  fieldOptions: FormFieldArrayOptions<T, K>;
}

export function isArrayProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & ArrayFormFieldFieldValue<T, K> {
  return props.type === GeneralFormFields.FIELD_ARRAY;
}

export interface SelectFieldValue<T, K extends keyof T, V = T[K]> {
  type: GeneralFormFields.SELECT_FIELD;
  fieldOptions: SelectFieldOptions<V>;
}

export function isSelectProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & SelectFieldValue<T, K> {
  return props.type === GeneralFormFields.SELECT_FIELD;
}

export interface RichTextFieldValue<T, K extends keyof T, V = EditorState> {
  type: GeneralFormFields.RICH_TEXT_FIELD;
  fieldOptions: RichTextFieldOptions;
}

export function isRichTextProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & RichTextFieldValue<T, K> {
  return props.type === GeneralFormFields.RICH_TEXT_FIELD;
}

export interface DateTimeFieldValue<T, K extends keyof T, V = Date> {
  type: GeneralFormFields.DATE_TIME_FIELD;
  fieldOptions: DateTimeFormFieldOptions;
}

export function isDateTimeProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & DateTimeFieldValue<T, K> {
  return props.type === GeneralFormFields.DATE_TIME_FIELD;
}

export interface ToggleFieldValue<T, K extends keyof T, V = boolean> {
  type: GeneralFormFields.TOGGLE_FIELD;
  fieldOptions: ToggleFormFieldOptions;
}

export function isToggleFieldProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & ToggleFieldValue<T, K> {
  return props.type === GeneralFormFields.TOGGLE_FIELD;
}

export interface LocationFieldValue<T, K extends keyof T, V = MapLocation> {
  type: GeneralFormFields.LOCATION_FIELD;
  fieldOptions: LocationFormFieldOptions;
}

export type ColorFieldOptions = {
};

export interface ColorFieldValue {
  type: GeneralFormFields.COLOR_FIELD;
  fieldOptions: ColorFieldOptions;
}

export function isLocationProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & LocationFieldValue<T, K> {
  return props.type === GeneralFormFields.LOCATION_FIELD;
}

export function isColorProps<
  T,
  K extends keyof T,
  >(props: FieldProps<T, K>): props is FieldPropsCommon<T, K> & ColorFieldValue {
  return props.type === GeneralFormFields.COLOR_FIELD;
}

export enum FieldFileCommonTypes {
  IMAGE,
  VIDEO,
}

export type FieldFileTypes = FieldFileCommonTypes | 'other';

export interface RemoteFile {
  source: string;
  type: FieldFileTypes;
}

export interface LocalFile {
  file: File;
  type: FieldFileTypes;
}

export function isRemoteFile(fieldFile: FieldFile): fieldFile is RemoteFile {
  return !!(fieldFile as RemoteFile).source;
}

export type FieldFile = LocalFile | RemoteFile;
